import { useEffect, useRef, useState } from 'react';
import { refreshAccessToken } from '@/shared/auth/auth.reducer';
import { getAccessTokenExpiryMs } from '@/shared/auth/jwt-storage';
import { useAppDispatch } from '@/store';
/**
 * Proactively refresh access token ~2 minutes before expiry (Monolithic_App_Frontend refresh-token hook).
 */
export function useRefreshAccessToken(enabled) {
    const dispatch = useAppDispatch();
    const [scheduleEpoch, bumpScheduleEpoch] = useState(0);
    const timerRef = useRef(null);
    /** Refresh this many milliseconds before JWT expiry */
    const earlyRefreshMs = 2 * 60 * 1000;
    useEffect(() => {
        const handler = () => {
            bumpScheduleEpoch((n) => n + 1);
        };
        window.addEventListener('callcenter-iam-tokens-updated', handler);
        return () => window.removeEventListener('callcenter-iam-tokens-updated', handler);
    }, []);
    useEffect(() => {
        if (!enabled)
            return;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        const expireAtMs = typeof window !== 'undefined' ? getAccessTokenExpiryMs() : null;
        if (expireAtMs) {
            const now = Date.now();
            const refreshAtMs = expireAtMs - earlyRefreshMs;
            const timeoutMs = refreshAtMs - now;
            if (timeoutMs > 0) {
                timerRef.current = setTimeout(() => {
                    void dispatch(refreshAccessToken()).unwrap().catch(() => {
                        /* 401 clears session via interceptor */
                    });
                }, timeoutMs);
            }
            else {
                void dispatch(refreshAccessToken()).unwrap().catch(() => {
                    /* interceptor */
                });
            }
        }
        /**
         * `scheduleEpoch` bumps when JWT storage updates (`callcenter-iam-tokens-updated`), so timers reschedule after rotate.
         */
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [dispatch, enabled, scheduleEpoch]);
}
